import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, Plus, Loader2, Ticket, Users, Clock, XCircle, CheckCircle2, Pencil, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { shieldBlockedMessage } from "@/lib/shield";
import { errText } from "@/lib/errText";
import { timeUntil } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Avatar } from "@/components/common/Avatar";
import { VerifiedCheck } from "@/components/common/TrustBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const usd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtWhen = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};
// <input type="datetime-local"> value <-> ISO, honoring local timezone.
const isoToLocal = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 60 * 60 * 1000);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const defaultStart = () => {
  const d = new Date(Date.now() + 60 * 60 * 1000);
  d.setMinutes(d.getMinutes() > 30 ? 60 : 30, 0, 0);
  return isoToLocal(d.toISOString());
};

// Create OR edit an event. Edit only touches PATCH-able fields (title/description/starts_at/capacity);
// price + duration are set at creation and not editable per the API contract.
function EventDialog({ bloqId, event, open, onOpenChange, onDone }) {
  const isEdit = !!event;
  const [title, setTitle] = useState(event?.title || "");
  const [startsAt, setStartsAt] = useState(event ? isoToLocal(event.starts_at) : defaultStart());
  const [description, setDescription] = useState(event?.description || "");
  const [duration, setDuration] = useState(event?.duration_min ? String(event.duration_min) : "60");
  const [price, setPrice] = useState(event?.price_usd != null ? String(event.price_usd) : "0");
  const [capacity, setCapacity] = useState(event?.capacity != null ? String(event.capacity) : "");
  const [busy, setBusy] = useState(false);

  const priceNum = Number(price) || 0;
  // Future-start required only when creating; edits (e.g. fixing a title on a past/ongoing event) are allowed.
  const ready = title.trim().length >= 3 && !!startsAt && (isEdit || new Date(startsAt).getTime() > Date.now());

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      if (isEdit) {
        await api.updateEvent(event.id, {
          title: title.trim(),
          starts_at: new Date(startsAt).toISOString(),
          description: description.trim(),
          capacity: capacity ? Number(capacity) : null,
        });
        toast.success("Event updated");
      } else {
        await api.createBloqEvent(bloqId, {
          title: title.trim(),
          starts_at: new Date(startsAt).toISOString(),
          description: description.trim() || undefined,
          duration_min: Number(duration) || 60,
          price_usd: priceNum,
          capacity: capacity ? Number(capacity) : undefined,
        });
        toast.success("Event scheduled");
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      const st = e?.response?.status;
      const blocked = shieldBlockedMessage(e);
      if (blocked) toast.error(blocked);
      else if (st === 422) toast.error("Capacity can't be lower than tickets already sold.");
      else if (st === 403) toast.error("Paid events need an approved creator account with payouts set up.");
      else toast.error(errText(e, isEdit ? "Couldn't update event." : "Couldn't create event."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-white/10 bg-bloq-s1 text-white" data-testid={isEdit ? "edit-event-dialog" : "create-event-dialog"}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <CalendarDays className="h-4 w-4 text-bloq-purple" /> {isEdit ? "Edit event" : "Schedule an event"}
          </DialogTitle>
          <DialogDescription className="text-bloq-muted">
            {isEdit ? "Update the details. Price can't be changed after creation." : "Host a ticketed session for this bloq. Set price to $0 for a free RSVP."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="e.g. Live AMA with the founders" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="event-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Starts</Label>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="event-start-input" />
            </div>
            {!isEdit && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-bloq-muted">Duration (min)</Label>
                <Input type="number" min={5} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="event-duration-input" />
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-bloq-muted">Description (optional)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} placeholder="What's this event about?" className="min-h-20 rounded-xl border-white/12 bg-bloq-bg" data-testid="event-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {!isEdit ? (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-bloq-muted">Price (USD)</Label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-bloq-muted">$</span>
                  <Input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="h-11 rounded-xl border-white/12 bg-bloq-bg pl-6" data-testid="event-price-input" />
                </div>
                <p className="text-[10px] text-bloq-muted">$0 = free RSVP</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-bloq-muted">Price</Label>
                <div className="flex h-11 items-center rounded-xl border border-white/8 bg-bloq-s2 px-3 text-sm text-bloq-muted" data-testid="event-price-locked">
                  {event.free || Number(event.price_usd) === 0 ? "Free" : `${usd(event.price_usd)} (locked)`}
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-bloq-muted">Capacity (optional)</Label>
              <Input type="number" min={1} value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="Unlimited" className="h-11 rounded-xl border-white/12 bg-bloq-bg" data-testid="event-capacity-input" />
              {isEdit && event.tickets_sold > 0 && <p className="text-[10px] text-bloq-muted">Can't go below {event.tickets_sold} sold</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={!ready || busy} className="w-full rounded-full bloq-gradient font-bold text-bloq-bg hover:opacity-90 disabled:opacity-60" data-testid="event-submit-btn">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : isEdit ? "Save changes" : "Schedule event"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttendeesDialog({ event, open, onOpenChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ["event-attendees", event?.id],
    queryFn: () => api.eventAttendees(event.id),
    enabled: open && !!event?.id,
    retry: false,
  });
  const attendees = data?.attendees || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="attendees-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Users className="h-4 w-4 text-bloq-purple" /> Guests
          </DialogTitle>
          <DialogDescription className="text-bloq-muted">
            {event?.title} · <span data-testid="attendees-count">{data?.count ?? attendees.length}</span> going
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : attendees.length === 0 ? (
            <p className="py-8 text-center text-sm text-bloq-muted" data-testid="attendees-empty">No guests yet.</p>
          ) : (
            <div className="space-y-1" data-testid="attendees-list">
              {attendees.map((a, i) => (
                <div key={a.user?.id || i} className="flex items-center gap-3 rounded-2xl px-2 py-2 hover:bg-white/5" data-testid={`attendee-${a.user?.id || i}`}>
                  {a.user && <Avatar user={a.user} size="sm" />}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium">{a.user?.display_name || `@${a.user?.username}`}</span>
                      {a.user?.verified && <VerifiedCheck size={12} />}
                    </div>
                    <div className="text-xs text-bloq-muted">@{a.user?.username}</div>
                  </div>
                  <span className={cn("shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider", a.paid ? "border-bloq-mint/30 bg-bloq-mint/10 text-bloq-mint" : "border-white/15 bg-white/5 text-bloq-muted")}>
                    {a.paid ? "Paid" : "Free"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EventCard({ ev, isOwner, busy, inviteCode, onGetTicket, onCancel, onEdit, onViewGuests }) {
  const canceled = ev.status === "canceled";
  const hasTicket = !!ev.my_ticket;
  const soldOut = ev.sold_out;
  const free = ev.free || Number(ev.price_usd) === 0;

  const shareEvent = async (e) => {
    e?.stopPropagation?.();
    const url = api.eventShareUrl(ev.id, inviteCode);
    try {
      if (navigator.share) {
        await navigator.share({ title: ev.title, text: `Join "${ev.title}" on BLOQ Talk`, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success("Event link copied");
    } catch (err) {
      if (err?.name === "AbortError") return; // user dismissed the native share sheet
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Event link copied");
      } catch {
        toast.error("Couldn't share the event");
      }
    }
  };

  return (
    <div className={cn("rounded-2xl border p-3.5", canceled ? "border-white/8 bg-bloq-s2/50 opacity-70" : "border-white/8 bg-bloq-s2")} data-testid={`event-row-${ev.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-white" data-testid={`event-title-${ev.id}`}>{ev.title}</span>
            {canceled && <span className="rounded-full border border-destructive/30 bg-destructive/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-destructive" data-testid={`event-canceled-${ev.id}`}>Canceled</span>}
            {!canceled && free && <span className="rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-bloq-mint">Free</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-bloq-muted">
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtWhen(ev.starts_at)}</span>
            {!free && <span className="font-semibold text-white" data-testid={`event-price-${ev.id}`}>{usd(ev.price_usd)}</span>}
            {ev.capacity != null && <span data-testid={`event-cap-${ev.id}`}>{ev.tickets_sold || 0}/{ev.capacity} sold</span>}
          </div>
        </div>
        {!canceled && (
          <div className="flex shrink-0 items-center gap-1">
            <button onClick={shareEvent} className="rounded-lg p-1.5 text-bloq-muted hover:bg-white/5 hover:text-bloq-mint" title="Share event" data-testid={`event-share-${ev.id}`}>
              <Share2 className="h-4 w-4" />
            </button>
            {isOwner && (
              <button onClick={() => onEdit(ev)} className="rounded-lg p-1.5 text-bloq-muted hover:bg-white/5 hover:text-white" title="Edit event" data-testid={`event-edit-${ev.id}`}>
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>
        )}
      </div>
      {ev.description && <p className="mt-2 text-xs text-white/75">{ev.description}</p>}

      {!canceled && (
        <div className="mt-3 flex items-center gap-2">
          {hasTicket ? (
            <span className="flex items-center gap-1.5 rounded-full border border-bloq-mint/30 bg-bloq-mint/10 px-3 py-1.5 text-xs font-bold text-bloq-mint" data-testid={`event-has-ticket-${ev.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5" /> You're going <span className="font-semibold opacity-80" data-testid={`event-startsin-${ev.id}`}>· {timeUntil(ev.starts_at)}</span>
            </span>
          ) : soldOut ? (
            <span className="rounded-full border border-white/12 px-3 py-1.5 text-xs font-semibold text-bloq-muted" data-testid={`event-soldout-${ev.id}`}>Sold out</span>
          ) : (
            <Button onClick={() => onGetTicket(ev)} disabled={busy} className="rounded-full bg-bloq-purple px-4 text-xs font-bold text-white hover:bg-bloq-purple/90 disabled:opacity-60" data-testid={`event-get-ticket-${ev.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : free ? <><Ticket className="mr-1 h-3.5 w-3.5" /> RSVP</> : <><Ticket className="mr-1 h-3.5 w-3.5" /> Get ticket</>}
            </Button>
          )}
          {isOwner && (
            <>
              <button onClick={() => onViewGuests(ev)} className="flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[11px] font-semibold text-bloq-muted hover:text-white" data-testid={`event-guests-${ev.id}`}>
                <Users className="h-3.5 w-3.5" /> {ev.tickets_sold || 0} going
              </button>
              <button onClick={() => onCancel(ev)} disabled={busy} className="ml-auto flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-[11px] font-semibold text-bloq-muted hover:text-destructive disabled:opacity-40" data-testid={`event-cancel-${ev.id}`}>
                <XCircle className="h-3.5 w-3.5" /> Cancel
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Lane B — ticketed events for a bloq. Shown inside BloqInfoDrawer.
export function EventsSection({ bloq, isOwner, open }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null); // null | { event: null } (create) | { event } (edit)
  const [guestsFor, setGuestsFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["bloq-events", bloq.id],
    queryFn: () => api.bloqEvents(bloq.id),
    enabled: open && !!bloq.id,
    retry: false,
  });
  const events = data?.events || [];
  // My invite code — appended as ?ref= on event share links for referral attribution.
  const { data: invite } = useQuery({
    queryKey: ["my-invite"],
    queryFn: api.myInvite,
    enabled: open,
    staleTime: 300000,
    retry: false,
  });
  const inviteCode = invite?.code;
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["bloq-events", bloq.id] });
    qc.invalidateQueries({ queryKey: ["me-upcoming-events"] });
  };

  const getTicket = async (ev) => {
    setBusyId(ev.id);
    try {
      const res = await api.eventTicketCheckout(ev.id, { origin_url: window.location.origin });
      if (res.url) { window.location.href = res.url; return; }
      if (res.granted) { toast.success("You're in! Ticket confirmed."); invalidate(); }
      else toast.error("Couldn't get a ticket.");
    } catch (e) {
      const st = e?.response?.status;
      if (st === 409) toast.error("You already have a ticket, or it's sold out.");
      else if (st === 410) toast.error("This event was canceled or has already passed.");
      else if (st === 503) toast.error("The host isn't set up to accept payments yet.");
      else toast.error(errText(e, "Couldn't get a ticket."));
    } finally {
      setBusyId(null);
    }
  };

  const cancelEvent = async (ev) => {
    setBusyId(ev.id);
    try {
      await api.updateEvent(ev.id, { status: "canceled" });
      toast.success("Event canceled");
      invalidate();
    } catch (e) {
      toast.error(errText(e, "Couldn't cancel event."));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-6" data-testid="bloq-events-section">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
          <CalendarDays className="h-3.5 w-3.5 text-bloq-purple" /> Events
        </div>
        {isOwner && (
          <button onClick={() => setDialog({ event: null })} className="flex items-center gap-1 rounded-full border border-white/12 px-2.5 py-1 text-[11px] font-semibold text-bloq-mint hover:bg-bloq-mint/10" data-testid="new-event-btn">
            <Plus className="h-3.5 w-3.5" /> New
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6 text-bloq-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : events.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-white/10 py-5 text-center text-xs text-bloq-muted" data-testid="events-empty">
          No upcoming events{isOwner ? " — schedule one to bring your bloq together." : "."}
        </p>
      ) : (
        <div className="space-y-2" data-testid="events-list">
          {events.map((ev) => (
            <EventCard
              key={ev.id}
              ev={ev}
              isOwner={isOwner}
              busy={busyId === ev.id}
              inviteCode={inviteCode}
              onGetTicket={getTicket}
              onCancel={cancelEvent}
              onEdit={(e) => setDialog({ event: e })}
              onViewGuests={(e) => setGuestsFor(e)}
            />
          ))}
        </div>
      )}

      {dialog && (
        <EventDialog
          key={dialog.event?.id || "new"}
          bloqId={bloq.id}
          event={dialog.event}
          open
          onOpenChange={(o) => { if (!o) setDialog(null); }}
          onDone={invalidate}
        />
      )}
      {guestsFor && (
        <AttendeesDialog event={guestsFor} open onOpenChange={(o) => { if (!o) setGuestsFor(null); }} />
      )}
    </div>
  );
}
